/**
 * A shared file's page (/{ws}/f/{fileId}): the preview, name/size/type, who shared it, when and where, who can access
 * it and what I can do (download, or why not; join the channel). Permissions come from the server, which computes
 * them with logic/files `fileAccess` — the same function the API enforces.
 */
import type { FileAudience, FileInfoResponse, FilePermissions, FileRef } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { joinNames } from "../logic/format.ts";
import { audienceLines, downloadBlockedReason, fileIcon, fileKind, imageSource, kindLabel, type FileKind } from "../logic/files.ts";
import { formatUrl } from "../logic/shell.ts";
import { memberName, type WorkspaceCache } from "../logic/workspace.ts";
import type { LiveQuery } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type FilePreview = "image" | "video" | "pdf" | "card";

export type FileViewState = {
  fileId: string;
  /** "notFound": deleted, or not shared with me (the server doesn't say which). */
  status: "loading" | "ready" | "notFound" | "error";
  /** No connection and nothing cached. */
  offline: boolean;
  file?: FileRef;
  kind: FileKind;
  icon: string;
  kindLabel: MessageDescriptor;
  preview: FilePreview;
  /** What the preview shows (the original, or the thumbnail for download-only images). */
  previewSrc?: string;
  uploader?: { userId: string; name: string; avatarUrl?: string; isAgent: boolean };
  /** When it was shared (or uploaded, when not shared yet). */
  at?: number;
  /** "#design", "Conversation with Ada", "Page Roadmap", "Not shared yet". */
  where: MessageDescriptor;
  /** Opens the conversation / page it was shared in, when I can see it. */
  openUrl?: string;
  /** "Who can access" lines. */
  audience: MessageDescriptor[];
  permissions: FilePermissions;
  /** Why Download is off (absent when allowed). */
  downloadReason?: MessageDescriptor;
  /** Why the preview isn't shown inline. */
  previewReason?: MessageDescriptor;
  /** "Join #design" when I may join the channel it was shared in. */
  joinLabel?: MessageDescriptor;
  joining: boolean;
};

const NO_PERMISSIONS: FilePermissions = { canView: false, canPreview: false, canDownload: false, canJoin: false, reasons: [] };

export class FileViewModel extends ViewModel<FileViewState> {
  private readonly ws: WorkspaceViewModel;
  private readonly query: LiveQuery<FileInfoResponse>;

  constructor(ws: WorkspaceViewModel, fileId: string) {
    super({
      fileId,
      status: "loading",
      offline: false,
      kind: "other",
      icon: "file",
      kindLabel: kindLabel("other"),
      preview: "card",
      where: msg("files.page.notShared"),
      audience: [],
      permissions: NO_PERMISSIONS,
      joining: false,
    });
    this.ws = ws;
    this.query = ws.deps.data.fileInfo(ws.wsId, fileId);
    this.onDispose(this.query.subscribe(() => this.recompute()));
    this.onDispose(() => this.query.dispose());
    this.onDispose(ws.subscribe(() => this.recompute()));
    this.recompute();
  }

  private recompute(): void {
    const q = this.query.getSnapshot();
    const cache = this.ws.state.cache;
    const info = q.data;
    const offline = !this.ws.state.network;
    if (info === undefined) {
      const status = q.status === "error" ? (q.errorKind === "notFound" || q.errorKind === "forbidden" ? "notFound" : "error") : "loading";
      this.update({ status, offline: offline && q.status !== "success" });
      return;
    }
    const file = info.file;
    const kind = fileKind(file.mime, file.name);
    const p = info.permissions;
    const channelName = info.audience.kind === "channel" ? info.audience.channelName : undefined;
    const me = cache === undefined ? undefined : cache.members[cache.meId];
    const next: FileViewState = {
      fileId: this.state.fileId,
      status: "ready",
      offline,
      file,
      kind,
      icon: fileIcon(kind),
      kindLabel: kindLabel(kind),
      preview: "card",
      at: info.sharedAt ?? info.createdAt,
      where: this.where(info.audience, cache),
      audience: audienceLines(info.audience, this.dmNames(info.audience, cache)),
      permissions: p,
      joining: this.state.joining,
    };
    if (p.canPreview) {
      next.preview = kind === "image" ? "image" : kind === "video" ? "video" : kind === "pdf" ? "pdf" : "card";
      if (next.preview !== "card") next.previewSrc = kind === "image" ? imageSource(file) : file.url;
    } else if (p.canView && kind === "image" && file.thumbUrl !== undefined) {
      next.preview = "image";
      next.previewSrc = file.thumbUrl;
    }
    if (!p.canPreview && p.reasons.includes("preview_blocked")) next.previewReason = msg("files.reason.previewBlocked");
    const reason = downloadBlockedReason(p, channelName, me !== undefined && me.kind === "agent");
    if (reason !== null) next.downloadReason = reason;
    if (p.canJoin && channelName !== undefined) next.joinLabel = msg("files.page.join", { channel: channelName });
    const uploader = cache === undefined ? undefined : cache.members[info.uploaderId];
    next.uploader = { userId: info.uploaderId, name: cache === undefined ? info.uploaderId : memberName(cache, info.uploaderId), isAgent: uploader !== undefined && uploader.kind === "agent" };
    if (uploader !== undefined && uploader.avatarUrl !== undefined) next.uploader.avatarUrl = uploader.avatarUrl;
    const url = this.openUrl(info, cache);
    if (url !== undefined) next.openUrl = url;
    this.setState(next);
  }

  private dmNames(a: FileAudience, cache: WorkspaceCache | undefined): string {
    if (a.kind !== "dm" || cache === undefined) return "";
    return joinNames((a.userIds ?? []).map((id) => memberName(cache, id)));
  }

  private where(a: FileAudience, cache: WorkspaceCache | undefined): MessageDescriptor {
    switch (a.kind) {
      case "channel":
        return msg("files.page.whereChannel", { channel: a.channelName ?? "" });
      case "dm": {
        const others = cache === undefined ? a.userIds ?? [] : (a.userIds ?? []).filter((u) => u !== cache.meId);
        return msg("files.page.whereDm", { names: cache === undefined ? "" : joinNames(others.map((id) => memberName(cache, id))) });
      }
      case "page":
        return msg("files.page.wherePage", { title: a.pageTitle ?? "" });
      case "private":
        return msg("files.page.notShared");
      case "workspace":
        return msg("files.page.whereSticker");
    }
  }

  /** The conversation (at the message's thread, for replies) or page it lives in, if I can open it. */
  private openUrl(info: FileInfoResponse, cache: WorkspaceCache | undefined): string | undefined {
    if (cache === undefined || !info.permissions.canView) return undefined;
    const slug = cache.workspace.slug;
    const a = info.audience;
    if ((a.kind === "channel" || a.kind === "dm") && a.channelId !== undefined) {
      if (info.threadRootId !== undefined) return formatUrl({ type: "thread", ws: slug, channelId: a.channelId, rootId: info.threadRootId });
      return formatUrl({ type: "channel", ws: slug, channelId: a.channelId });
    }
    if (a.kind === "page" && a.pageId !== undefined) return formatUrl({ type: "page", ws: slug, pageId: a.pageId });
    return undefined;
  }

  // ---------- Intents ----------

  refresh(): void {
    void this.query.refresh();
  }

  /** Joins the channel the file was shared in, then reloads the permissions. */
  async join(): Promise<void> {
    const info = this.query.getSnapshot().data;
    const channelId = info === undefined ? undefined : info.audience.channelId;
    if (channelId === undefined || this.state.joining || info === undefined || !info.permissions.canJoin) return;
    this.update({ joining: true });
    try {
      await this.ws.deps.api.joinChannel(this.ws.wsId, channelId);
      await this.ws.refresh();
      await this.query.refresh();
    } catch {
      // Stays as it was; the page shows the same "join" option again.
    } finally {
      this.update({ joining: false });
    }
  }
}
